import { useForm } from '@inertiajs/react'
import Layout from '../../layouts/Layout'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Card from '../../components/ui/Card'
import type { ChangeEvent, FormEvent, ReactNode } from 'react'
import { useState } from 'react'

interface Props {
  errors?: Record<string, string[]>
}

export default function New({ errors: serverErrors }: Props) {
  const [preview, setPreview] = useState<string | null>(null)

  const { data, setData, post, processing, errors } = useForm({
    post: {
      title: '',
      image: null as File | null,
      remote_image_url: '',
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/posts', {
      forceFormData: true,
    })
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setData('post', { ...data.post, image: file })
      setPreview(URL.createObjectURL(file))
    }
  }

  const getError = (field: string) => {
    return errors[field as keyof typeof errors] || serverErrors?.[field]?.[0]
  }

  return (
    <Layout title="New Post">
      <div className="container mx-auto px-4 py-8 max-w-lg">
        <h1 className="text-3xl font-bold mb-8">New Post</h1>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-black file:text-white hover:file:bg-gray-800"
              />
              {getError('image') && (
                <p className="mt-1 text-sm text-red-600">{getError('image')}</p>
              )}
            </div>

            {/* Image Preview */}
            {preview && (
              <div>
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full rounded-lg"
                />
              </div>
            )}

            {/* Remote Image URL */}
            <Input
              label="Or Remote Image URL"
              value={data.post.remote_image_url}
              onChange={(e) => setData('post', { ...data.post, remote_image_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
              error={getError('remote_image_url')}
            />

            {/* Title */}
            <Input
              label="Title"
              value={data.post.title}
              onChange={(e) => setData('post', { ...data.post, title: e.target.value })}
              placeholder="Give your photo a title..."
              error={getError('title')}
            />

            <Button type="submit" disabled={processing} className="w-full">
              {processing ? 'Creating...' : 'Create Post'}
            </Button>
          </form>
        </Card>
      </div>
    </Layout>
  )
}

New.layout = (page: ReactNode) => page
